import React, { useState } from "react";
import { styled, css } from "styled-components";
import { IfPermitted, Timestamp, LinkContainer } from "components/common";
import SearchLink from "views/logic/search/SearchLink";
import { createElasticsearchQueryString } from "views/logic/queries/Query";
import { Row, Col, Table, Button, Badge, BootstrapModalConfirm, ButtonToolbar } from "components/bootstrap";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
const RetrievedStatusBadge = styled(Badge)(
  ({ theme }) => css`
    margin-left: ${theme.spacings.xxs};
  `
);
const StyledTbody = styled.tbody`
  ${({ $showIndices, theme }) => $showIndices && css`
      border-left: 4px solid ${theme.colors.variant.info};
    `}
  & > tr:is(:first-child) {
    cursor: pointer;
  }
`;
const StyledTable = styled(Table)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.sm};
    .table-hover > tbody > tr:hover {
      background: none;
    }
  `
);
const StyledDetailTr = styled.tr(
  ({ theme }) => css`
    & > td {
      padding-left: ${theme.spacings.lg} !important;
      padding-top: ${theme.spacings.sm} !important;
    }
  `
);
const DataLakeRetrievalTableEntry = ({ retrievalOperation, index, onDeleteOperation }) => {
  const [showIndices, setShowIndices] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const sendTelemetry = useSendTelemetry();
  const {
    retrieved_at,
    partially_retrieved,
    retrieved_documents,
    index_failures,
    timestamp_from,
    timestamp_to,
    restore_indices
  } = retrievalOperation;
  const onConfirmDelete = () => {
    onDeleteOperation(retrievalOperation.id);
  };
  const handleDelete = (event) => {
    event.stopPropagation();
    setShowConfirmDelete(true);
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.RETRIEVAL_OPERATION_DELETE_OPENED, {});
  };
  const getIndicesQueryString = (restoreIndices) => restoreIndices.reduce((acc, indice, currentIndex, arr) => {
    if (currentIndex + 1 === arr.length) {
      return `${acc}_index:${indice.index_name}`;
    }
    return `${acc}_index:${indice.index_name} OR `;
  }, "");
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledTbody, { $showIndices: showIndices }, /* @__PURE__ */ React.createElement("tr", { key: retrieved_at, onClick: () => setShowIndices(!showIndices) }, /* @__PURE__ */ React.createElement("td", null, index + 1), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: retrieved_at })), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(RetrievedStatusBadge, { bsStyle: partially_retrieved ? "warning" : "default" }, partially_retrieved ? "partially" : "fully", " restored")), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: timestamp_from })), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: timestamp_to })), /* @__PURE__ */ React.createElement("td", null, retrieved_documents), /* @__PURE__ */ React.createElement("td", null, index_failures), /* @__PURE__ */ React.createElement("td", null, restore_indices.length), /* @__PURE__ */ React.createElement("td", { "aria-label": "Actions" }, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(
    LinkContainer,
    {
      key: `received-messages-${index}`,
      to: SearchLink.builder().query(createElasticsearchQueryString(getIndicesQueryString(restore_indices))).timerange({
        type: "absolute",
        from: timestamp_from,
        to: timestamp_to
      }).build().toURL()
    },
    /* @__PURE__ */ React.createElement(
      Button,
      {
        bsSize: "xsmall",
        onClick: () => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.RETRIEVAL_OPERATION_VIEW_CLICKED, {});
        }
      },
      "Show messages"
    )
  ), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["data_lake_archive:delete"] }, /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", bsStyle: "danger", onClick: handleDelete }, "Delete"))))), showIndices && /* @__PURE__ */ React.createElement(StyledDetailTr, null, /* @__PURE__ */ React.createElement("td", { colSpan: 9, "aria-label": "Retrieval operation details" }, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 6 }, /* @__PURE__ */ React.createElement("p", null, "List of indices created during the retrieval operation."), /* @__PURE__ */ React.createElement(StyledTable, { condensed: true }, /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("th", null, "Indices"), /* @__PURE__ */ React.createElement("th", null, "Action"))), /* @__PURE__ */ React.createElement("tbody", null, restore_indices.map((indice) => /* @__PURE__ */ React.createElement("tr", { key: indice.index_name }, /* @__PURE__ */ React.createElement("td", null, indice.index_name), /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(
    LinkContainer,
    {
      key: `received-messages-${indice.index_name}`,
      to: SearchLink.builder().query(createElasticsearchQueryString(`_index:${indice.index_name}`)).timerange({
        type: "absolute",
        from: timestamp_from,
        to: timestamp_to
      }).build().toURL()
    },
    /* @__PURE__ */ React.createElement(Button, { bsSize: "xsmall", "aria-label": `Show messages for ${indice.index_name}` }, "Show messages")
  ))))))))))), showConfirmDelete && /* @__PURE__ */ React.createElement(
    BootstrapModalConfirm,
    {
      showModal: showConfirmDelete,
      onConfirm: () => onConfirmDelete(),
      onCancel: () => setShowConfirmDelete(false),
      title: "Are you sure?"
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "You are about to delete retrieved messages from", " ", /* @__PURE__ */ React.createElement("b", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: retrieved_at })), ". This will delete ", /* @__PURE__ */ React.createElement("b", null, restore_indices == null ? void 0 : restore_indices.length), " indices."), /* @__PURE__ */ React.createElement("p", null, "Do you want to continue?"))
  ));
};
export default DataLakeRetrievalTableEntry;
